import React, { useRef } from 'react';
import { Input } from '@leadsnavi/ui-kit';
import { toast } from '@leadsnavi/ui-kit';

interface SubjectInputProps {
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
  onFocus?: () => void;
  onBlur?: () => void;
}

/**
 * 邮件主题输入框组件
 * 支持粘贴超过字符数限制时自动截断并提示
 */
const SubjectInput: React.FC<SubjectInputProps> = ({
  value,
  onChange,
  maxLength = 256,
  placeholder = 'Please enter the Subject',
  className = '!bg-white border-none shadow-none hover:shadow-none hover:border-none focus-visible:ring-0 focus-visible:ring-offset-0',
  style,
  onFocus,
  onBlur,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * 处理粘贴事件
   * 当粘贴的内容超过最大长度时，截断并提示用户
   */
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    const currentValue = value || '';
    const newValue = currentValue + pastedText;

    if (newValue.length > maxLength) {
      e.preventDefault();
      // 计算可以粘贴的字符数
      const availableLength = maxLength - currentValue.length;
      if (availableLength > 0) {
        // 截断粘贴的内容
        const truncatedText = pastedText.substring(0, availableLength);
        onChange(currentValue + truncatedText);
        toast.info(`Subject has been truncated to ${maxLength} characters`);
      } else {
        // 如果当前值已经达到最大长度，只提示
        toast.info(`Subject cannot exceed ${maxLength} characters`);
      }
    }
  };

  /**
   * 处理输入变化
   * 确保输入不超过最大长度
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      onChange(newValue);
    } else {
      // 如果输入超过最大长度，截断并提示
      const truncatedValue = newValue.substring(0, maxLength);
      onChange(truncatedValue);
      toast.info(`Subject has been truncated to ${maxLength} characters`);
    }
  };

  return (
    <Input
      ref={inputRef}
      value={value}
      maxLength={maxLength}
      onChange={handleChange}
      onPaste={handlePaste}
      onFocus={onFocus}
      onBlur={onBlur}
      placeholder={placeholder}
      className={className}
      style={style}
    />
  );
};

export default SubjectInput;
